//#region IMPORT
import React, { useMemo, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  TextField,
} from '@mui/material';
import { DatePicker } from '../../../../Components/MUIs';
import SerEvalDeleteConfirmDialog, {
  DELETE_CONFIRM_TYPE,
} from './SerEvalDeleteConfirmDialog';
import SerEvalFinalSaveConfirmDialog from './SerEvalFinalSaveConfirmDialog';
import SerEvalExcelDownloadDialog from './SerEvalExcelDownloadDialog';
import { EXCEL_SHEET_MODE } from './SER_BASIC_01_Excel';
import {
  SumIndicatorVal3,
  SumCategoryMaxScore,
  GetCategoryMaxScore,
  GetCheckpointScore,
  GetObtainedByCheck,
  FormatIntegerScore,
} from '../serBasicScore';
import './SER_BASIC_01.styles.css';
//#endregion

const FORM_STATUS_LABEL = {
  DRAFT: '작성중',
  ACTIVE: '현재 적용',
  ARCHIVED: '보관',
};

const EVA_STATUS_LABEL = {
  DRAFT: '작성중',
  SAVED: '임시저장',
  CONFIRMED: '확정',
};

const FormatEvaStatusLabel = (status) => {
  const key = String(status ?? '').trim().toUpperCase();
  return EVA_STATUS_LABEL[key] || (key ? status : '');
};

const FormatFormStatusLabel = (status) => {
  const key = String(status ?? '').trim().toUpperCase();
  return FORM_STATUS_LABEL[key] || (key ? status : '');
};

const CHECKPOINT_LABELS = 'abcdefghijklmnopqrstuvwxyz';

/** 착안사항 라벨 (a, b, c …) */
const GetCheckpointLabel = (index) =>
  CHECKPOINT_LABELS[index] || String(index + 1);

/** number input — 마우스 휠로 값 변경 방지 */
const PreventScoreInputWheel = (e) => {
  e.currentTarget.blur();
};

const scoreInputProps = {
  min: 0,
  step: 1,
  onWheel: PreventScoreInputWheel,
};

function SER_BASIC_01(props) {
  const {
    steps = [],
    activeStep = 0,
    setActiveStep,
    arrayList = [],
    arrayList2 = [],
    HandleCheckboxChange,
    totalPoint = 0,
    dateValue = {},
    setDateValue,
    navigate,
    SaveLogic1,
    ExcelDownloadAll,
    custNm = '',
    AddCategoryLogic,
    UpdateCategoryLogic,
    DeleteCategoryLogic,
    AddIndicatorLogic,
    UpdateIndicatorLogic,
    DeleteIndicatorLogic,
    UpdateIndicatorPointLogic,
    UpdateCheckpointPointLogic,
    UpdateCategoryMaxScoreLogic,
    AddCheckpointLogic,
    UpdateCheckpointLogic,
    DeleteCheckpointLogic,
    readOnlyStructure = false,
    isTemplateMode = false,
    screenMode = 'evaluate',
    formName = '',
    setFormName,
    formVer = '',
    formStatus = '',
    templateReadOnly = false,
    isNewForm = false,
    PublishTemplateLogic,
    evaStatus = 'DRAFT',
    evaluationReadOnly = false,
    SaveDraftLogic,
    SaveFinalLogic,
    finalSaveConfirmOpen = false,
    setFinalSaveConfirmOpen,
    ConfirmFinalSaveLogic,
  } = props;

  const canEditStructure = !readOnlyStructure && !templateReadOnly;
  const canEditEvaluation = !isTemplateMode && !evaluationReadOnly;

  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [editingIndicatorCode, setEditingIndicatorCode] = useState(null);
  const [indicatorDraft, setIndicatorDraft] = useState('');
  const [editingCheckpointCode, setEditingCheckpointCode] = useState(null);
  const [checkpointDraft, setCheckpointDraft] = useState('');
  const [excelDownloadDialogOpen, setExcelDownloadDialogOpen] = useState(false);
  const [excelSheetMode, setExcelSheetMode] = useState(EXCEL_SHEET_MODE.SPLIT);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmContent, setDeleteConfirmContent] = useState({
    type: DELETE_CONFIRM_TYPE.CHECKPOINT,
    title: '',
    targetName: '',
    message: '',
  });
  const deleteConfirmActionRef = useRef(null);

  const OpenDeleteConfirm = ({ type, title, targetName, message, onConfirm }) => {
    deleteConfirmActionRef.current = onConfirm;
    setDeleteConfirmContent({ type, title, targetName, message });
    setDeleteConfirmOpen(true);
  };

  const HandleDeleteConfirmClose = () => {
    deleteConfirmActionRef.current = null;
    setDeleteConfirmOpen(false);
  };

  const HandleDeleteConfirmSave = () => {
    deleteConfirmActionRef.current?.();
    deleteConfirmActionRef.current = null;
    setDeleteConfirmOpen(false);
  };

  const HandleExcelDownloadOpen = () => {
    setExcelSheetMode(EXCEL_SHEET_MODE.SPLIT);
    setExcelDownloadDialogOpen(true);
  };

  const HandleExcelDownloadClose = () => {
    setExcelDownloadDialogOpen(false);
  };

  const HandleExcelDownloadConfirm = async () => {
    setExcelDownloadDialogOpen(false);
    await ExcelDownloadAll?.(excelSheetMode);
  };

  const stepNo = activeStep + 1;
  const stepKey = String(stepNo);

  const currentCategoryLabel = steps[activeStep]?.label || '';

  const stepIndicators = useMemo(
    () => arrayList.filter((row) => String(row.val1) === stepKey),
    [arrayList, stepKey]
  );

  const stepSummary = useMemo(() => {
    const indicatorMaxScore = stepIndicators.reduce(
      (sum, row) => sum + Number(row.val2 || 0),
      0
    );
    const maxScore = GetCategoryMaxScore(activeStep, steps, arrayList);
    const obtainedScore = stepIndicators.reduce(
      (sum, row) => sum + Number(row.val3 || 0),
      0
    );
    const indicatorCodes = new Set(stepIndicators.map((row) => row.code));
    const checkpointCount = arrayList2.filter((row) =>
      indicatorCodes.has(row.groupCd)
    ).length;
    const progress =
      maxScore > 0 ? Math.min(100, Math.round((obtainedScore / maxScore) * 100)) : 0;

    return {
      maxScore: FormatIntegerScore(maxScore),
      indicatorMaxScore: FormatIntegerScore(indicatorMaxScore),
      obtainedScore: FormatIntegerScore(obtainedScore),
      indicatorCount: stepIndicators.length,
      checkpointCount,
      progress,
    };
  }, [stepIndicators, arrayList2, activeStep, steps, arrayList]);

  /** 전체 배점(가점 제외 대분류 총배점 합) · 취득 · 가점 */
  const totalSummary = useMemo(() => {
    const totalMaxScore = FormatIntegerScore(
      SumCategoryMaxScore(steps, arrayList, { excludeBonus: true })
    );
    const baseObtained = FormatIntegerScore(
      SumIndicatorVal3(arrayList, steps, { excludeBonus: true })
    );
    const bonusObtained = FormatIntegerScore(
      SumIndicatorVal3(arrayList, steps, { onlyBonus: true })
    );
    const totalObtained = FormatIntegerScore(baseObtained + bonusObtained);
    const obtainRate =
      totalMaxScore > 0
        ? Math.round((totalObtained / totalMaxScore) * 100)
        : 0;

    return {
      totalMaxScore,
      totalObtained,
      baseObtained,
      bonusObtained,
      obtainRate,
    };
  }, [arrayList, steps]);

  const GetCategoryDisplayName = useCallback(
    (index) => steps[index]?.label || '',
    [steps]
  );

  const HandleAddCategory = (e) => {
    e?.stopPropagation?.();
    const newIndex = AddCategoryLogic?.();
    if (newIndex == null) return;
    setEditingCategoryIndex(newIndex);
    setCategoryDraft(`신규 대분류 ${newIndex + 1}`);
  };

  const HandleCategoryEditStart = (index, e) => {
    e.stopPropagation();
    setEditingCategoryIndex(index);
    setCategoryDraft(GetCategoryDisplayName(index));
  };

  const HandleCategoryEditSave = (index, e) => {
    e?.stopPropagation?.();
    const next = categoryDraft.trim();
    if (!next) return;
    if (UpdateCategoryLogic?.(index, next)) {
      setEditingCategoryIndex(null);
      setCategoryDraft('');
    }
  };

  const HandleCategoryDelete = (index, e) => {
    e?.stopPropagation?.();
    const label = GetCategoryDisplayName(index);
    OpenDeleteConfirm({
      type: DELETE_CONFIRM_TYPE.CATEGORY,
      title: '대분류 삭제',
      targetName: label,
      onConfirm: () => {
        DeleteCategoryLogic?.(index);
        setEditingCategoryIndex(null);
        setCategoryDraft('');
      },
    });
  };

  const HandleCategoryDeleteActive = () => {
    const label = currentCategoryLabel;
    OpenDeleteConfirm({
      type: DELETE_CONFIRM_TYPE.CATEGORY,
      title: '대분류 삭제',
      targetName: label,
      onConfirm: () => {
        DeleteCategoryLogic?.(activeStep);
        setEditingCategoryIndex(null);
        setCategoryDraft('');
      },
    });
  };

  const HandleCategoryEditCancel = (e) => {
    e?.stopPropagation?.();
    setEditingCategoryIndex(null);
    setCategoryDraft('');
  };

  const HandleIndicatorEditStart = (indicator) => {
    setEditingIndicatorCode(indicator.code);
    setIndicatorDraft(indicator.codeNm || '');
  };

  const HandleIndicatorEditSave = (indicator) => {
    const next = indicatorDraft.trim();
    if (!next) return;
    if (UpdateIndicatorLogic?.(indicator.code, next)) {
      setEditingIndicatorCode(null);
      setIndicatorDraft('');
    }
  };

  const HandleCheckpointEditStart = (checkpoint) => {
    setEditingCheckpointCode(checkpoint.code);
    setCheckpointDraft(checkpoint.codeNm || '');
  };

  const HandleCheckpointEditSave = (checkpoint) => {
    const next = checkpointDraft.trim();
    if (!next) return;
    if (UpdateCheckpointLogic?.(checkpoint.code, next)) {
      setEditingCheckpointCode(null);
      setCheckpointDraft('');
    }
  };

  const HandleAddIndicator = () => {
    const code = AddIndicatorLogic?.(activeStep);
    if (!code) return;
    setEditingIndicatorCode(code);
    setIndicatorDraft('신규 평가지표');
  };

  const HandleDeleteIndicator = (indicator) => {
    OpenDeleteConfirm({
      type: DELETE_CONFIRM_TYPE.INDICATOR,
      title: '평가지표 삭제',
      targetName: indicator.codeNm,
      onConfirm: () => {
        DeleteIndicatorLogic?.(indicator);
        if (editingIndicatorCode === indicator.code) {
          setEditingIndicatorCode(null);
          setIndicatorDraft('');
        }
      },
    });
  };

  const HandleAddCheckpoint = (indicator) => {
    const code = AddCheckpointLogic?.(indicator);
    if (!code) return;
    setEditingCheckpointCode(code);
    setCheckpointDraft('신규 착안사항');
  };

  const HandleDeleteCheckpoint = (checkpoint, indicator) => {
    OpenDeleteConfirm({
      type: DELETE_CONFIRM_TYPE.CHECKPOINT,
      title: '착안사항 삭제',
      targetName: checkpoint.codeNm,
      onConfirm: () => {
        DeleteCheckpointLogic?.(checkpoint, indicator);
        if (editingCheckpointCode === checkpoint.code) {
          setEditingCheckpointCode(null);
          setCheckpointDraft('');
        }
      },
    });
  };

  const HandleCheckpointEditCancel = () => {
    setEditingCheckpointCode(null);
    setCheckpointDraft('');
  };

  const HandleIndicatorEditCancel = () => {
    setEditingIndicatorCode(null);
    setIndicatorDraft('');
  };

  const RenderIconButton = ({
    icon,
    title,
    onClick,
    className = '',
    filled = false,
  }) => (
    <button
      type="button"
      className={`ser-eval-icon-btn ${className}`}
      title={title}
      onClick={onClick}
    >
      <span className={`material-symbols-outlined ${filled ? 'icon-fill' : ''}`}>
        {icon}
      </span>
    </button>
  );

  return (
    <div className="ser-eval-layout">
      {/* 좌측: 대분류(Step) */}
      <aside className="ser-eval-sidebar">
        <div className="ser-eval-sidebar__workspace">
          <div className="ser-eval-sidebar__workspace-icon">
            <span className="material-symbols-outlined icon-fill">factory</span>
          </div>
          <div>
            <div className="ser-eval-sidebar__workspace-label">사업장</div>
            <div className="ser-eval-sidebar__workspace-name">
              {custNm || '선택된 사업장'}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="ser-eval-sidebar__new-btn"
          onClick={() => navigate?.('/SeriousAccidentAct')}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            arrow_back
          </span>
          평가 목록
        </button>

        <nav className="ser-eval-sidebar__nav custom-scrollbar">
          <div className="ser-eval-sidebar__section-title">
            <span>대분류</span>
            {canEditStructure && (
              <button
                type="button"
                className="ser-eval-sidebar__section-add"
                title="대분류 추가"
                onClick={HandleAddCategory}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                  add
                </span>
              </button>
            )}
          </div>

          <div>
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              const isEditing = editingCategoryIndex === index;
              const label = GetCategoryDisplayName(index);

              return (
                <div
                  key={step.id || `${step.label}-${index}`}
                  className={`ser-eval-category ${isActive ? 'is-active' : ''} ${isEditing ? 'is-editing' : ''}`}
                  onClick={() => {
                    if (!isEditing) setActiveStep?.(index);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isEditing) setActiveStep?.(index);
                  }}
                >
                  {isEditing ? (
                    <>
                      <input
                        className="ser-eval-inline-input"
                        value={categoryDraft}
                        onChange={(e) => setCategoryDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') HandleCategoryEditSave(index, e);
                          if (e.key === 'Escape') HandleCategoryEditCancel(e);
                        }}
                      />
                      <div className="ser-eval-category__actions">
                        <RenderIconButton
                          icon="check"
                          title="저장"
                          className="is-success"
                          onClick={(e) => HandleCategoryEditSave(index, e)}
                        />
                        <RenderIconButton
                          icon="close"
                          title="취소"
                          onClick={HandleCategoryEditCancel}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="ser-eval-category__label">
                        {index + 1}. {label}
                      </span>
                      {canEditStructure && (
                        <div className="ser-eval-category__actions">
                          <RenderIconButton
                            icon="edit"
                            title="수정"
                            onClick={(e) => HandleCategoryEditStart(index, e)}
                          />
                          <RenderIconButton
                            icon="delete"
                            title="삭제"
                            className="is-danger"
                            onClick={(e) => HandleCategoryDelete(index, e)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </aside>

      {/* 우측: 평가 구조 */}
      <main className="ser-eval-main">
        <div className="ser-eval-main__scroll custom-scrollbar">
          <div className="ser-eval-toolbar">
            <div className="ser-eval-toolbar__left">
              {isTemplateMode && (
                <TextField
                  size="small"
                  label="양식명"
                  placeholder="예) 2026년 중대재해 자체평가"
                  value={formName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 100) {
                      setFormName?.(value);
                    }
                  }}
                  disabled={!canEditStructure}
                  inputProps={{ maxLength: 100 }}
                  sx={{ width: 280, bgcolor: '#fff', mr: 1 }}
                />
              )}
              {(isNewForm || formName || formVer || formStatus) && (
                <span className="ser-eval-badge" style={{ marginRight: 8 }}>
                  {isNewForm
                    ? '신규 양식'
                    : `${isTemplateMode ? '양식 편집' : '선택 양식'} · ${formName || '양식'}${formStatus ? ` · ${FormatFormStatusLabel(formStatus)}` : ''}`}
                </span>
              )}
              {!isTemplateMode && evaStatus && (
                <span
                  className={`ser-eval-badge ${String(evaStatus).toUpperCase() === 'CONFIRMED' ? 'is-confirmed' : ''}`}
                  style={{ marginRight: 8 }}
                >
                  평가상태 · {FormatEvaStatusLabel(evaStatus)}
                  {evaluationReadOnly ? ' (수정 불가)' : ''}
                </span>
              )}
              <button
                type="button"
                className="ser-eval-btn ser-eval-btn--outline"
                onClick={HandleExcelDownloadOpen}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                  download
                </span>
                일괄 엑셀다운로드
              </button>
            </div>
            <div className="ser-eval-toolbar__right">
              <TextField
                size="small"
                placeholder="평가자"
                value={dateValue.evaName || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 20) {
                    setDateValue?.({ ...dateValue, evaName: value });
                  }
                }}
                inputProps={{ maxLength: 20, readOnly: !canEditEvaluation }}
                error={!dateValue.evaName?.trim()}
                disabled={!canEditEvaluation}
                sx={{ width: 140, bgcolor: '#fff' }}
              />
              <DatePicker
                sx={{ width: 160 }}
                name="평가일자"
                type="datePicker"
                disabled={!canEditEvaluation}
                defaultValue={
                  dateValue.evaDate ? dayjs(dateValue.evaDate) : dayjs()
                }
                onChange={(date) => {
                  if (date) {
                    setDateValue?.({
                      ...dateValue,
                      evaDate: dayjs(date).format('YYYY-MM-DD'),
                    });
                  }
                }}
                slotProps={{ textField: { size: 'small' } }}
                currentLocale="ko"
              />
            </div>
          </div>

          <header className="ser-eval-page-header">
            <div className="ser-eval-page-header__top">
              <div>
                <div className="ser-eval-breadcrumb">
                  <span className="ser-eval-badge">대분류 {stepNo}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                    chevron_right
                  </span>
                  <span>
                    {isTemplateMode
                      ? '양식 구조 편집'
                      : readOnlyStructure
                        ? '평가 진행'
                        : '평가 항목 관리'}
                  </span>
                </div>
                <h1 className="ser-eval-page-title">
                  {stepNo}. {currentCategoryLabel}
                  {canEditStructure && (
                    <RenderIconButton
                      icon="edit"
                      title="대분류명 수정"
                      onClick={() => {
                        setEditingCategoryIndex(activeStep);
                        setCategoryDraft(currentCategoryLabel);
                      }}
                    />
                  )}
                </h1>
              </div>
              {canEditStructure && (
                <button
                  type="button"
                  className="ser-eval-btn ser-eval-btn--outline-danger"
                  onClick={HandleCategoryDeleteActive}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    delete
                  </span>
                  대분류 삭제
                </button>
              )}
            </div>

            <div className="ser-eval-summary">
              <div className="ser-eval-summary__stats">
                <div>
                  <div className="ser-eval-summary__stat-label">대분류 총배점</div>
                  <div className="ser-eval-summary__stat-value">
                    {canEditStructure ? (
                      <input
                        className="ser-eval-score-input"
                        type="number"
                        {...scoreInputProps}
                        value={stepSummary.maxScore}
                        onChange={(e) =>
                          UpdateCategoryMaxScoreLogic?.(
                            activeStep,
                            e.target.value
                          )
                        }
                        title="대분류 총배점 직접 설정"
                      />
                    ) : (
                      stepSummary.maxScore
                    )}
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#9e9e9e',
                        marginLeft: 4,
                      }}
                    >
                      점
                    </span>
                  </div>
                  {canEditStructure &&
                    stepSummary.indicatorMaxScore !== stepSummary.maxScore && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#9e9e9e',
                          marginTop: 4,
                        }}
                      >
                        지표 합계 {stepSummary.indicatorMaxScore}점
                      </div>
                    )}
                </div>
                <div className="ser-eval-summary__divider" />
                <div>
                  <div className="ser-eval-summary__stat-label">대분류 취득</div>
                  <div className="ser-eval-summary__stat-value is-primary">
                    {stepSummary.obtainedScore}
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#9e9e9e',
                        marginLeft: 4,
                      }}
                    >
                      / {stepSummary.maxScore}점
                    </span>
                  </div>
                </div>
                <div className="ser-eval-summary__divider" />
                <div>
                  <div className="ser-eval-summary__stat-label">평가지표</div>
                  <div className="ser-eval-summary__stat-value">
                    {stepSummary.indicatorCount}
                  </div>
                </div>
                <div className="ser-eval-summary__divider" />
                <div>
                  <div className="ser-eval-summary__stat-label">착안사항</div>
                  <div className="ser-eval-summary__stat-value">
                    {stepSummary.checkpointCount}
                  </div>
                </div>
              </div>
              <div className="ser-eval-summary__progress">
                <div className="ser-eval-summary__progress-head">
                  <span style={{ color: '#757575' }}>대분류 취득률</span>
                  <span
                    style={{
                      color:
                        stepSummary.progress >= 100
                          ? 'var(--ser-success)'
                          : 'var(--ser-warning)',
                      fontWeight: 600,
                    }}
                  >
                    {stepSummary.progress}%
                  </span>
                </div>
                <div className="ser-eval-summary__progress-bar">
                  <div
                    className={`ser-eval-summary__progress-fill ${stepSummary.progress >= 100 ? '' : 'is-warning'}`}
                    style={{ width: `${stepSummary.progress}%` }}
                  />
                </div>
              </div>
            </div>
          </header>

          <div className="ser-eval-table-wrap">
            <div className="ser-eval-table-toolbar">
              <div className="ser-eval-table-toolbar__title">평가 구조</div>
              {canEditStructure && (
                <button
                  type="button"
                  className="ser-eval-btn ser-eval-btn--soft"
                  onClick={HandleAddIndicator}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                    add_circle
                  </span>
                  평가지표 추가
                </button>
              )}
            </div>

            <div
              className="ser-eval-table-head"
              style={
                !canEditStructure
                  ? { gridTemplateColumns: '72px 1fr 140px 80px 80px' }
                  : undefined
              }
            >
              <div>소계</div>
              <div>평가지표 · 착안사항</div>
              <div>평가</div>
              <div>배점</div>
              <div>취득</div>
              {canEditStructure && <div>관리</div>}
            </div>

            {stepIndicators.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#757575' }}>
                <p style={{ marginBottom: 12 }}>이 대분류에 등록된 평가 항목이 없습니다.</p>
                {canEditStructure && (
                  <button
                    type="button"
                    className="ser-eval-btn ser-eval-btn--soft"
                    onClick={HandleAddIndicator}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      add_circle
                    </span>
                    첫 평가지표 추가
                  </button>
                )}
              </div>
            ) : (
              stepIndicators.map((indicator, indicatorIndex) => {
                const checkpoints = arrayList2.filter(
                  (row) => row.groupCd === indicator.code
                );
                const subtotalLabel = `${indicator.val1}-${indicatorIndex + 1}`;
                const obtainedNum = Number(indicator.val3 || 0);
                const maxNum = Number(indicator.val2 || 0);
                const subScoreClass =
                  obtainedNum >= maxNum
                    ? 'is-success'
                    : obtainedNum > 0
                      ? 'is-warning'
                      : '';

                return (
                  <div key={indicator.code}>
                    <div className="ser-eval-table-row ser-eval-table-row--indicator group">
                      <div className="ser-eval-index">{subtotalLabel}</div>
                      <div className="ser-eval-text-cell">
                        {canEditStructure && editingIndicatorCode === indicator.code ? (
                          <>
                            <input
                              className="ser-eval-inline-input"
                              value={indicatorDraft}
                              onChange={(e) => setIndicatorDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') HandleIndicatorEditSave(indicator);
                                if (e.key === 'Escape') HandleIndicatorEditCancel();
                              }}
                            />
                            <RenderIconButton
                              icon="check"
                              title="저장"
                              className="is-success"
                              onClick={() => HandleIndicatorEditSave(indicator)}
                            />
                            <RenderIconButton
                              icon="close"
                              title="취소"
                              onClick={HandleIndicatorEditCancel}
                            />
                          </>
                        ) : (
                          <>
                            <span>{indicator.codeNm}</span>
                            {canEditStructure && (
                              <RenderIconButton
                                icon="edit"
                                title="수정"
                                onClick={() => HandleIndicatorEditStart(indicator)}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <div />
                      <div style={{ textAlign: 'right' }}>
                        {canEditStructure ? (
                          <input
                            className="ser-eval-score-input"
                            type="number"
                            {...scoreInputProps}
                            value={FormatIntegerScore(indicator.val2 ?? 0)}
                            onChange={(e) =>
                              UpdateIndicatorPointLogic?.(
                                indicator.code,
                                e.target.value
                              )
                            }
                            title="평가지표 소계 배점 (하위 착안사항에 균등 분배)"
                          />
                        ) : (
                          <span>{FormatIntegerScore(indicator.val2 ?? 0)}</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className={`ser-eval-sub-score ${subScoreClass}`}>
                          취득{' '}
                        </span>
                        <span className={subScoreClass}>{FormatIntegerScore(indicator.val3 ?? 0)}</span>
                      </div>
                      {canEditStructure && (
                        <div className="ser-eval-row-actions">
                          <RenderIconButton
                            icon="delete"
                            title="평가지표 삭제"
                            className="is-danger"
                            onClick={() => HandleDeleteIndicator(indicator)}
                          />
                        </div>
                      )}
                    </div>

                    <div className="ser-eval-checkpoints">
                      {checkpoints.map((checkpoint, cpIndex) => {
                        const check = checkpoint.check || '0';
                        const checkpointScore = GetCheckpointScore(
                          checkpoint,
                          indicator,
                          checkpoints
                        );
                        const obtained = GetObtainedByCheck(check, checkpointScore);
                        const isEditingCp = editingCheckpointCode === checkpoint.code;
                        const obtainedClass =
                          check === 'N' ? 'is-warning' : 'is-success';

                        return (
                          <div
                            key={checkpoint.code}
                            className="ser-eval-table-row ser-eval-table-row--checkpoint"
                          >
                            <div className="ser-eval-checkpoint-index">
                              <span className="branch">└</span>
                              {GetCheckpointLabel(cpIndex)}
                            </div>
                            <div className="ser-eval-text-cell">
                              {canEditStructure && isEditingCp ? (
                                <>
                                  <input
                                    className="ser-eval-inline-input"
                                    value={checkpointDraft}
                                    onChange={(e) =>
                                      setCheckpointDraft(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        HandleCheckpointEditSave(checkpoint);
                                      }
                                      if (e.key === 'Escape') {
                                        HandleCheckpointEditCancel();
                                      }
                                    }}
                                  />
                                  <RenderIconButton
                                    icon="check"
                                    title="저장"
                                    className="is-success"
                                    onClick={() =>
                                      HandleCheckpointEditSave(checkpoint)
                                    }
                                  />
                                  <RenderIconButton
                                    icon="close"
                                    title="취소"
                                    onClick={HandleCheckpointEditCancel}
                                  />
                                </>
                              ) : (
                                <>
                                  <span style={{ color: '#616161' }}>
                                    {checkpoint.codeNm}
                                  </span>
                                  {canEditStructure && (
                                    <RenderIconButton
                                      icon="edit"
                                      title="수정"
                                      onClick={() =>
                                        HandleCheckpointEditStart(checkpoint)
                                      }
                                    />
                                  )}
                                </>
                              )}
                            </div>
                            <div className="ser-eval-radio-group">
                              {[
                                { name: '양호', value: 'Y' },
                                { name: '미흡', value: 'N' },
                                { name: '해당없음', value: '0' },
                              ].map((opt) => (
                                <label key={opt.value}>
                                  <input
                                    type="radio"
                                    name={`cp-${checkpoint.code}`}
                                    checked={check === opt.value}
                                    disabled={!canEditEvaluation}
                                    onChange={() =>
                                      HandleCheckboxChange?.(checkpoint, opt.value)
                                    }
                                  />
                                  {opt.name}
                                </label>
                              ))}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {canEditStructure ? (
                                <input
                                  className="ser-eval-score-input"
                                  type="number"
                                  {...scoreInputProps}
                                  value={
                                    checkpoint.val2 != null && checkpoint.val2 !== ''
                                      ? FormatIntegerScore(checkpoint.val2)
                                      : FormatIntegerScore(checkpointScore)
                                  }
                                  onChange={(e) =>
                                    UpdateCheckpointPointLogic?.(
                                      checkpoint.code,
                                      e.target.value
                                    )
                                  }
                                  title="착안사항 배점 직접 설정"
                                />
                              ) : (
                                <span>{FormatIntegerScore(checkpointScore)}</span>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <input
                                className={`ser-eval-score-input is-readonly ${obtainedClass}`}
                                type="number"
                                value={FormatIntegerScore(obtained)}
                                readOnly
                                onWheel={PreventScoreInputWheel}
                              />
                            </div>
                            {canEditStructure && (
                              <div className="ser-eval-row-actions">
                                <RenderIconButton
                                  icon="close"
                                  title="착안사항 삭제"
                                  className="is-danger"
                                  onClick={() =>
                                    HandleDeleteCheckpoint(checkpoint, indicator)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {canEditStructure && (
                        <div className="ser-eval-add-checkpoint">
                          <button
                            type="button"
                            onClick={() => HandleAddCheckpoint(indicator)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: 14 }}
                            >
                              add
                            </span>
                            {subtotalLabel} 착안사항 추가
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <footer className="ser-eval-footer">
          <div className="ser-eval-footer__left">
            <div
              className={`ser-eval-validation ${totalSummary.totalMaxScore > 0 ? 'is-ok' : 'is-warn'}`}
            >
              <span
                className="material-symbols-outlined icon-fill"
                style={{ fontSize: 18 }}
              >
                {totalSummary.totalMaxScore > 0 ? 'check_circle' : 'warning'}
              </span>
              {totalSummary.totalMaxScore > 0
                ? `총점 ${totalSummary.totalObtained}/${totalSummary.totalMaxScore}점 (${totalSummary.obtainRate}%)`
                : '전체 배점이 없습니다. 대분류·평가지표 배점을 확인해 주세요.'}
            </div>
            <span className="ser-eval-footer__hint">
              전체 배점 {totalSummary.totalMaxScore}점(가점 제외) · 취득{' '}
              {totalSummary.totalObtained}점
              {totalSummary.bonusObtained > 0
                ? ` (기본 ${totalSummary.baseObtained} + 가점 ${totalSummary.bonusObtained})`
                : ''}
            </span>
          </div>
          <div className="ser-eval-footer__actions">
            {isTemplateMode &&
              String(formStatus).toUpperCase() === 'DRAFT' &&
              !templateReadOnly && (
                <button
                  type="button"
                  className="ser-eval-btn ser-eval-btn--outline"
                  onClick={() => PublishTemplateLogic?.()}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    publish
                  </span>
                  현재 버전을 적용하기
                </button>
              )}
            {!templateReadOnly && (
              <>
                {isTemplateMode ? (
                  <button
                    type="button"
                    className="ser-eval-btn ser-eval-btn--primary"
                    onClick={() => SaveLogic1?.()}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                      save
                    </span>
                    양식 저장
                  </button>
                ) : (
                  <>
                    {!evaluationReadOnly && (
                      <>
                        <button
                          type="button"
                          className="ser-eval-btn ser-eval-btn--outline"
                          onClick={() => SaveDraftLogic?.()}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: 18 }}
                          >
                            draft
                          </span>
                          임시저장
                        </button>
                        <button
                          type="button"
                          className="ser-eval-btn ser-eval-btn--primary"
                          onClick={() => SaveFinalLogic?.()}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: 18 }}
                          >
                            verified
                          </span>
                          최종저장
                        </button>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </footer>
      </main>

      <SerEvalDeleteConfirmDialog
        open={deleteConfirmOpen}
        type={deleteConfirmContent.type}
        title={deleteConfirmContent.title}
        targetName={deleteConfirmContent.targetName}
        message={deleteConfirmContent.message}
        onConfirm={HandleDeleteConfirmSave}
        onClose={HandleDeleteConfirmClose}
      />

      <SerEvalFinalSaveConfirmDialog
        open={finalSaveConfirmOpen}
        formName={formName}
        formVer={formVer}
        evaName={dateValue?.evaName}
        evaDate={dateValue?.evaDate}
        totalPoint={totalPoint}
        custNm={custNm}
        onConfirm={() => ConfirmFinalSaveLogic?.()}
        onClose={() => setFinalSaveConfirmOpen?.(false)}
      />

      <SerEvalExcelDownloadDialog
        open={excelDownloadDialogOpen}
        sheetMode={excelSheetMode}
        onSheetModeChange={setExcelSheetMode}
        onClose={HandleExcelDownloadClose}
        onConfirm={HandleExcelDownloadConfirm}
      />
    </div>
  );
}

export default SER_BASIC_01;
